// @ts-strict-ignore
import List from 'antd/lib/list';
import * as React from 'react';
import { AppComponentProps } from '../../typescriptHelpers';
import styles from '../styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Icon, {
    CaretRightOutlined,
    ControlOutlined,
    NotificationOutlined,
    SettingOutlined,
    SortAscendingOutlined,
    ThunderboltFilled,
} from '@ant-design/icons';
import { antGrey, runwayColors, tagStyle } from '../styles/globalStyles';
import Button from 'antd/lib/button';
import { Automation, AutomationType } from '../../types';
import { setSeenNewAutomations, setSeenUpdatedAutomations } from '../../helpers/localStorageHelper';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import { iconForAutomationType } from '../../helpers/displayHelpers';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { AutomationPlanExecutionTooltipIcon } from '../planRestrictions/planRestrictionHelperComponents';
import Tag from 'antd/lib/tag';
import Search from 'antd/lib/input/Search';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { BucketAutomationSettings } from '../automations/BucketAutomationSettings';
import { AppAutomationSettings, BetaBadge, UpdatedBadge } from '../automations/AppAutomationSettings';
import { selectSortedFilteredAutomations } from '@/selectors/memoizedSelectors';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectBuildDistributionBucketById,
    selectIsWorkflowsCIStepEnabled,
    selectStepConfigDataFromStepGraphs,
} from '@/selectors/selectors';
import Collapse from 'antd/lib/collapse';
import { groupBy } from 'lodash';
import { InlineStepConfigDescription } from '@/components/common/InlineStepConfigDescription';
import { SortTableButton } from '@/components/tableFilters/SortTableButton';
import { SettingsSortType } from '@/types/sorts';
import { AutomationSettingsContext } from '@/helpers/automationHelpers';

interface AutomationsComponentProps extends AppComponentProps {
    refProp?: React.MutableRefObject<HTMLDivElement>;
    bucketId?: string;
    hideHeader?: boolean;
}

export const AutomationsComponent: React.FC<AutomationsComponentProps> = (props: AutomationsComponentProps) => {
    const { bucketId } = props;

    const location = useLocation();
    const history = useHistory();
    const params = qs.parse(location?.search);

    const automations = useSelector((state: RootState) => {
        if (props.bucketId) {
            return selectBuildDistributionBucketById(state, bucketId).automations;
        } else {
            return selectSortedFilteredAutomations(state, props.appId);
        }
    });

    const [showAutomationSettings, setShowAutomationSettings] = React.useState(false);
    const [automationIdSelectedForSettings, setAutomationIdSelectedForSettings] = React.useState(null);
    const [searchValue, setSearchValue] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);
    const [selectedSortType, setSelectedSortType] = React.useState<SettingsSortType>(SettingsSortType.Name);

    const automationIsEnabled = (automation: Automation) =>
        automation.enabled || Object.values(automation.configPerReleaseType ?? {}).some((override) => override.enabled);

    const processedAutomations = React.useMemo(() => {
        const filtered = automations.filter((automation) => {
            return automation.name.toLowerCase().includes(searchValue.toLowerCase());
        });

        return [...filtered].sort((a, b) => {
            switch (selectedSortType) {
                case SettingsSortType.Name:
                    return a.name.localeCompare(b.name);

                case SettingsSortType.EnabledFirst:
                    if (automationIsEnabled(a) !== automationIsEnabled(b)) {
                        return automationIsEnabled(a) ? -1 : 1;
                    }
                    return a.name.localeCompare(b.name);

                case SettingsSortType.NewUpdatedFirst: {
                    const isNewOrUpdated = (automation: Automation) => automation.isNew || automation.isUpdated;
                    if (isNewOrUpdated(a) !== isNewOrUpdated(b)) {
                        return isNewOrUpdated(a) ? -1 : 1;
                    }
                    return a.name.localeCompare(b.name);
                }
                default:
                    return 0;
            }
        });
    }, [searchValue, automations, selectedSortType]);

    const groupedAutomationsByType = React.useMemo(() => {
        return groupBy(processedAutomations, 'type') as Record<AutomationType, Automation[]>;
    }, [processedAutomations]);

    const stepGraphStepsMap = useSelector((state: RootState) => selectStepConfigDataFromStepGraphs(state, props.appId));

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, props.appId),
    );

    React.useEffect(() => {
        // save seen new/updated automations to local storage
        if (automations) {
            const seenNewAutomations: Partial<Record<AutomationType, true>> = {};
            const seenUpdatedAutomations: Partial<Record<AutomationType, true>> = {};
            automations.forEach((automation) => {
                if (automation.isNew) {
                    seenNewAutomations[automation.type] = true;
                }
                if (automation.isUpdated) {
                    seenUpdatedAutomations[automation.type] = true;
                }
            });
            setSeenNewAutomations(seenNewAutomations);
            setSeenUpdatedAutomations(seenUpdatedAutomations);
        }
    }, []);

    React.useEffect(() => {
        const automationId = params?.id;

        if (automationId) {
            setShowAutomationSettings(true);
            setAutomationIdSelectedForSettings(automationId);

            history.replace({ search: null });
        }
    });

    const platform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));

    const badgeAndSettings = ({
        automation,
        isDisabledDueToPricingPlan,
    }: {
        automation: Automation;
        isDisabledDueToPricingPlan: boolean;
    }) => {
        const isAutomationEnabled = automationIsEnabled(automation);
        const showPlanWontExecuteIcon = isDisabledDueToPricingPlan && isAutomationEnabled;
        const tagShowsEnabled = isAutomationEnabled && !isDisabledDueToPricingPlan;

        const statusTag = (
            <Tag
                color={runwayColors.white}
                style={{
                    ...tagStyle,
                    fontSize: 10,
                    borderColor: tagShowsEnabled ? runwayColors.statusGreen : antGrey.grey6,
                    color: tagShowsEnabled ? runwayColors.statusGreen : antGrey.grey6,
                }}
            >
                {tagShowsEnabled ? 'Enabled' : 'Disabled'}
            </Tag>
        );

        return (
            <Space align="center" size={12}>
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        columnGap: showPlanWontExecuteIcon ? 12 : 0,
                    }}
                >
                    <AutomationPlanExecutionTooltipIcon visible={showPlanWontExecuteIcon} />
                    {statusTag}
                </span>
                <Button
                    ghost
                    icon={
                        <SettingOutlined
                            style={{
                                color: isDisabledDueToPricingPlan ? antGrey.grey6 : runwayColors.info,
                            }}
                        />
                    }
                    onClick={() => {
                        setAutomationIdSelectedForSettings(automation.id);
                        setShowAutomationSettings(true);
                    }}
                />
            </Space>
        );
    };

    const automationRow = ({
        automation,
        isDisabledDueToPricingPlan,
        isEnabled,
        shouldShowBadgeAndSettings,
    }: {
        automation: Automation;
        isDisabledDueToPricingPlan: boolean;
        isEnabled: boolean;
        shouldShowBadgeAndSettings?: boolean;
    }) => {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexGrow: 1,
                }}
            >
                <Space size={18} align="center">
                    <Icon
                        disabled={isDisabledDueToPricingPlan}
                        component={iconForAutomationType(automation.type)}
                        style={{
                            fontSize: 16,
                            color: isEnabled ? antGrey.grey7 : antGrey.grey5,
                            paddingTop: 4,
                        }}
                    />
                    <Text disabled={isDisabledDueToPricingPlan} type={isEnabled ? undefined : 'secondary'}>
                        {automation.name}
                    </Text>
                    <PlanRestrictedFeatureWrapper automationType={automation.type} requiresTooltip />
                    <div style={{ display: 'flex', gap: '1px' }}>
                        {automation.isBeta && <BetaBadge />}{' '}
                        {automation.isNew && (
                            <Tag
                                color={runwayColors.primary}
                                style={{
                                    ...tagStyle,
                                    fontSize: 10,
                                    color: 'white',
                                }}
                            >
                                New
                            </Tag>
                        )}{' '}
                        {automation.isUpdated && <UpdatedBadge />}
                    </div>
                </Space>
                {shouldShowBadgeAndSettings &&
                    badgeAndSettings({
                        automation,
                        isDisabledDueToPricingPlan,
                    })}
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }} ref={props.refProp}>
            {!props.hideHeader && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Space size={8}>
                        <Text style={styles.appSettingsSectionTitleStyle}>Available automations</Text>
                        <ThunderboltFilled style={{ color: runwayColors.statusGrey }} />
                    </Space>
                    <Space>
                        <SortTableButton<SettingsSortType>
                            selectedSortType={selectedSortType}
                            sortOptions={[
                                {
                                    sortType: SettingsSortType.Name,
                                    text: 'Name',
                                    icon: <SortAscendingOutlined style={{ color: runwayColors.primary }} />,
                                },
                                {
                                    sortType: SettingsSortType.EnabledFirst,
                                    text: 'Enabled',
                                    icon: <ControlOutlined style={{ color: runwayColors.primary }} />,
                                },
                                {
                                    sortType: SettingsSortType.NewUpdatedFirst,
                                    text: 'Recently updated',
                                    icon: <NotificationOutlined style={{ color: runwayColors.primary }} />,
                                },
                            ]}
                            onSelectSort={(sortType) => {
                                setSelectedSortType(sortType);
                            }}
                        />
                        <Search
                            placeholder="Search automations"
                            allowClear
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onSearch={(value) => {
                                setSearchValue(value);
                            }}
                            style={{ width: 400 }}
                        />
                    </Space>
                </div>
            )}
            <List
                header={null}
                pagination={{
                    hideOnSinglePage: true,
                    defaultPageSize: 10,
                    current: currentPage,
                    onChange: (page) => setCurrentPage(page),
                }}
                dataSource={Object.entries(groupedAutomationsByType)}
                renderItem={([automationTypeString, automations]) => {
                    const automationType = automationTypeString as AutomationType;

                    const hasMultipleAutomationsOfTheSameType = automations.length > 1;
                    return (
                        <List.Item key={automationType}>
                            <PlanRestrictedFeatureWrapper automationType={automationType}>
                                {(isDisabledDueToPricingPlan) => {
                                    const isAnyAutomationEnabled = automations.some((automation) =>
                                        automationIsEnabled(automation),
                                    );
                                    return hasMultipleAutomationsOfTheSameType ?
                                            <Collapse
                                                className="automations-collapsible"
                                                defaultActiveKey={[]}
                                                style={{
                                                    flex: 1,
                                                }}
                                                ghost
                                                expandIcon={({ isActive }) => (
                                                    <CaretRightOutlined
                                                        style={{
                                                            color: antGrey.grey6,
                                                            fontSize: 14,
                                                        }}
                                                        rotate={isActive ? 90 : 0}
                                                    />
                                                )}
                                            >
                                                <Collapse.Panel
                                                    header={automationRow({
                                                        automation: automations[0],
                                                        isEnabled: isAnyAutomationEnabled,
                                                        isDisabledDueToPricingPlan,
                                                        shouldShowBadgeAndSettings: false,
                                                    })}
                                                    key={'1'}
                                                >
                                                    <List
                                                        bordered
                                                        size="small"
                                                        dataSource={automations}
                                                        renderItem={(automation) => {
                                                            const stepGraphStep =
                                                                stepGraphStepsMap[automation.stepConfigId];
                                                            if (!stepGraphStep) {
                                                                return null;
                                                            }
                                                            return (
                                                                <List.Item key={automation.id}>
                                                                    <div
                                                                        style={{
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center',
                                                                            gap: 16,
                                                                            width: '100%',
                                                                        }}
                                                                    >
                                                                        <InlineStepConfigDescription
                                                                            stepGraphStep={stepGraphStep}
                                                                            platform={platform}
                                                                            appStoreType={appStoreType}
                                                                            isWorkflowsCIStepEnabled={
                                                                                isWorkflowsCIStepEnabled
                                                                            }
                                                                        />
                                                                        {badgeAndSettings({
                                                                            automation,
                                                                            isDisabledDueToPricingPlan,
                                                                        })}
                                                                    </div>
                                                                </List.Item>
                                                            );
                                                        }}
                                                    />
                                                </Collapse.Panel>
                                            </Collapse>
                                        :   automationRow({
                                                automation: automations[0],
                                                isEnabled: isAnyAutomationEnabled,
                                                isDisabledDueToPricingPlan,
                                                shouldShowBadgeAndSettings: true,
                                            });
                                }}
                            </PlanRestrictedFeatureWrapper>
                        </List.Item>
                    );
                }}
            ></List>
            {automationIdSelectedForSettings &&
                (bucketId ?
                    <BucketAutomationSettings
                        appId={props.appId}
                        orgId={props.orgId}
                        bucketId={bucketId}
                        onClose={() => setShowAutomationSettings(false)}
                        open={showAutomationSettings}
                        automationType={automationIdSelectedForSettings}
                        onAfterClose={() => {
                            setAutomationIdSelectedForSettings(null);
                        }}
                    />
                :   <AppAutomationSettings
                        onAutomationSelected={(automationId) => setAutomationIdSelectedForSettings(automationId)}
                        appId={props.appId}
                        orgId={props.orgId}
                        onClose={() => setShowAutomationSettings(false)}
                        open={showAutomationSettings}
                        automationId={automationIdSelectedForSettings}
                        context={AutomationSettingsContext.App}
                        onAfterClose={() => {
                            setAutomationIdSelectedForSettings(null);
                        }}
                    />)}
        </div>
    );
};
